import type {
	TeamworkGraphPerson,
	TeamworkGraphPersonRelationship,
} from '@confluence/twg-graph-explorer/entry-points/teamworkGraphModel.ts';

import type { PagePerson, PagePersonRelationshipEvidence } from './parsePagePeople.ts';
import { PAGE_PEOPLE_RELATIONSHIPS } from './pagePeopleRelationships.ts';

/**
 * How many ranked people the canvas draws. A view-level cap, applied by
 * `TeamworkGraphCanvasPlaceholder` — ranking hands out `MAX_LISTED_PAGE_PEOPLE` and does not
 * know or care that one of its consumers wants fewer.
 */
export const MAX_PAGE_PEOPLE = 4;

/**
 * How many ranked people this package deals in: the most `PeopleAndAgentsSection` will ever list,
 * reached by expanding it. Everyone the ranking puts below the tenth is cut off — the section is a
 * summary, not a directory.
 */
export const MAX_LISTED_PAGE_PEOPLE = 10;

/**
 * A ranked person plus the account id, which `PeopleAndAgentsSection` needs for its profile link
 * and the canvas does not. Still a `TeamworkGraphPerson`, so the canvas takes it unchanged.
 */
export type RankedPagePerson = TeamworkGraphPerson & { accountId: string };

const CONFLUENCE_PAGE_LEVEL_ONE = new Set<TeamworkGraphPersonRelationship>([
	'created',
	'contributed',
]);
const FALLBACK_PRIORITY_LEVEL = 7;
const LEVEL_ONE_PRIORITY = 1;
const PRIORITY_WEIGHT = 1_000;
const RECENCY_HALF_LIFE_DAYS = 30;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1_000;
const RELATIONSHIP_ORDER = new Map(
	PAGE_PEOPLE_RELATIONSHIPS.map(({ relationship }, index) => [relationship, index]),
);

const priorityLevel = ({ relationship }: PagePersonRelationshipEvidence): number =>
	CONFLUENCE_PAGE_LEVEL_ONE.has(relationship) ? LEVEL_ONE_PRIORITY : FALLBACK_PRIORITY_LEVEL;

/**
 * The bounded decay term used by object expansion. Priority bands always dominate; recency orders
 * evidence within a band and naturally loses influence as the relationship ages.
 */
const recencyDecay = (lastUpdated: number | null, now: number): number => {
	if (lastUpdated == null || !Number.isFinite(lastUpdated)) {
		return 0;
	}

	const ageDays = Math.max(0, now - lastUpdated) / MILLISECONDS_PER_DAY;
	return Math.exp((-Math.LN2 * ageDays) / RECENCY_HALF_LIFE_DAYS);
};

const scoreRelationship = (relationship: PagePersonRelationshipEvidence, now: number): number =>
	(8 - priorityLevel(relationship)) * PRIORITY_WEIGHT + recencyDecay(relationship.lastUpdated, now);

const compareText = (left: string, right: string): number =>
	left < right ? -1 : left > right ? 1 : 0;

const compareRelationships = (
	left: PagePersonRelationshipEvidence,
	right: PagePersonRelationshipEvidence,
	now: number,
): number => {
	const scoreDifference = scoreRelationship(right, now) - scoreRelationship(left, now);
	if (scoreDifference !== 0) {
		return scoreDifference;
	}

	return (
		(RELATIONSHIP_ORDER.get(left.relationship) ?? Number.MAX_SAFE_INTEGER) -
		(RELATIONSHIP_ORDER.get(right.relationship) ?? Number.MAX_SAFE_INTEGER)
	);
};

type RankedPerson = {
	person: PagePerson;
	primaryRelationship: PagePersonRelationshipEvidence;
	score: number;
};

const pickPrimaryRelationship = (
	person: PagePerson,
	now: number,
): PagePersonRelationshipEvidence | undefined => {
	let primary: PagePersonRelationshipEvidence | undefined;
	for (const relationship of person.relationships) {
		if (!primary || compareRelationships(relationship, primary, now) < 0) {
			primary = relationship;
		}
	}
	return primary;
};

/**
 * Applies the page object-expansion priority bands and recency decay, then applies the dedicated
 * product cap. Generic graph type caps and clustering intentionally do not apply to this slice.
 *
 * The cap is `MAX_LISTED_PAGE_PEOPLE` for every caller: how many people a given surface shows is
 * that surface's business, so the canvas trims what it is given rather than asking for less.
 */
export const rankPagePeople = (
	people: readonly PagePerson[],
	now: number = Date.now(),
): RankedPagePerson[] => rankPagePeopleCandidates(people, now).slice(0, MAX_LISTED_PAGE_PEOPLE);

/** Rank all candidates so combined and people-only lists can be capped independently. */
export const rankPagePeopleCandidates = (
	people: readonly PagePerson[],
	now: number = Date.now(),
): RankedPagePerson[] => {
	const rankedPeople: RankedPerson[] = [];
	for (const person of people) {
		const primaryRelationship = pickPrimaryRelationship(person, now);
		if (primaryRelationship) {
			rankedPeople.push({
				person,
				primaryRelationship,
				score: scoreRelationship(primaryRelationship, now),
			});
		}
	}

	return rankedPeople
		.sort(
			(left, right) =>
				right.score - left.score ||
				compareText(left.person.ari, right.person.ari) ||
				compareText(left.person.accountId, right.person.accountId),
		)
		.map(({ person, primaryRelationship }) => ({
			ari: person.ari,
			accountId: person.accountId,
			name: person.name,
			picture: person.picture,
			...(person.jobTitle ? { jobTitle: person.jobTitle } : {}),
			primaryRelationship: primaryRelationship.relationship,
			relationshipDirection: primaryRelationship.direction,
		}));
};
